import type { ReactNode } from 'react';
import type { Page } from '@rossum/api-client/pages';
import {
  type Band,
  ContentSpacer,
  type LayoutBandHeights,
  type PageLayoutData,
  useDocumentSetup,
} from '../core';
import { useActivePageTracking } from '../features/active-page';
import { DocumentPageWrapper } from './DocumentPageWrapper';

export type DocumentContentProps = {
  pages: Page[];
  renderPageOverlays?: (pageModel: Page, layout: PageLayoutData) => ReactNode;
  bands?: LayoutBandHeights;
  renderHeader?: (band: Band) => ReactNode;
  renderBeforePage?: (
    pageModel: Page,
    layout: PageLayoutData,
    band: Band
  ) => ReactNode;
  renderAfterPage?: (
    pageModel: Page,
    layout: PageLayoutData,
    band: Band
  ) => ReactNode;
};

export const DocumentContent = ({
  pages,
  renderPageOverlays,
  bands,
  renderHeader,
  renderBeforePage,
  renderAfterPage,
}: DocumentContentProps) => {
  const {
    pageLayouts,
    contentWidth,
    contentHeight,
    headerBand,
    allPagesInitialized,
  } = useDocumentSetup(pages, bands);

  useActivePageTracking(pageLayouts);

  if (!allPagesInitialized) {
    return null;
  }

  return (
    <>
      <ContentSpacer width={contentWidth} height={contentHeight} />
      {headerBand && renderHeader ? (
        <g transform={`translate(${headerBand.x}, ${headerBand.y})`}>
          {renderHeader(headerBand)}
        </g>
      ) : null}
      {pages.map((page, index) => {
        const pageLayout = pageLayouts[index];
        if (!pageLayout) return null;

        return (
          <DocumentPageWrapper
            key={page.id}
            pageModel={page}
            layout={pageLayout}
            renderPageOverlays={renderPageOverlays}
          />
        );
      })}
      {/* Bands are cheap, static decorations (headers, page badges) whose */}
      {/* size feeds the layout, so they render for every page regardless of */}
      {/* visibility — unlike `renderPageOverlays`, which can be expensive and */}
      {/* is therefore gated on page visibility. */}
      {renderBeforePage || renderAfterPage
        ? pages.map((page, index) => {
            const pageLayout = pageLayouts[index];
            if (!pageLayout) return null;

            const { beforeBand, afterBand } = pageLayout;

            const before =
              beforeBand && renderBeforePage ? (
                <g transform={`translate(${beforeBand.x}, ${beforeBand.y})`}>
                  {renderBeforePage(page, pageLayout, beforeBand)}
                </g>
              ) : null;

            const after =
              afterBand && renderAfterPage ? (
                <g transform={`translate(${afterBand.x}, ${afterBand.y})`}>
                  {renderAfterPage(page, pageLayout, afterBand)}
                </g>
              ) : null;

            if (!before && !after) return null;

            return (
              <g key={`bands-${page.id}`}>
                {before}
                {after}
              </g>
            );
          })
        : null}
    </>
  );
};
